import React, { useEffect, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';

const DataLeaderAnimation = () => {
  const controls = useAnimationControls();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    
    const runAnimation = async () => {
      try {
        await controls.start("reset");
        await controls.start("animate");
        await new Promise(resolve => setTimeout(resolve, 3000));
        if (isMounted) {
          runAnimation();
        }
      } catch (error) {
        console.error("Animation error:", error);
      }
    };
    
    runAnimation();
  }, [isMounted, controls]);

  const chartPoints = "M40,80 L80,60 L120,70 L160,40 L200,50 L240,30";

  return (
    <div className="relative w-full overflow-hidden">
      <motion.div 
        style={{height: 'fit-content'}}
        className="bg-[#EDE9FE] rounded-xl p-4 sm:p-6 relative h-[500px] sm:h-[400px] flex flex-col sm:flex-row items-center transform-gpu"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* Container that changes from column (mobile) to row (desktop) */}
        <div className="flex flex-col sm:flex-row items-center w-full justify-center scale-[0.9] sm:scale-90 origin-top sm:origin-center">
          {/* User Figure - Positioned on top for mobile, left for desktop */}
          <div className="relative mb-2 sm:mb-0 sm:mr-4 flex flex-col items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={controls}
              variants={{
                reset: { opacity: 0, scale: 0.8 },
                animate: { opacity: 1, scale: 1, transition: { delay: 0.3 } }
              }}
              className="flex flex-col items-center"
            >
              {/* User Avatar - Smaller */}
              <div className="w-12 h-12 rounded-full bg-indigo-600 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              
              {/* Centered text */}
              <span className="text-xs font-medium text-gray-700 text-center mt-1">Data<br/>Leader</span>
            </motion.div>
          </div>

          {/* Main Dashboard Container - Full width on mobile */}
          <motion.div 
            className="bg-white rounded-lg shadow-lg w-full max-w-5xl overflow-hidden mt-0"
            initial={{ opacity: 0, y: 20, x: 0 }}
            animate={controls}
            variants={{
              reset: { opacity: 0, y: 20, x: 0 },
              animate: { 
                opacity: 1, 
                y: 0, 
                x: 0,
                transition: { delay: 1 } 
              }
            }}
          >
            {/* Dashboard Header */}
            <div className="bg-purple-600 p-3 flex items-center">
              <div className="flex space-x-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <span className="text-white text-sm ml-3">Data Quality Dashboard</span>
            </div>

            {/* Dashboard Content */}
            <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
              {/* Metrics and Chart Row */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {/* Metrics Section - Reduced padding and spacing */}
                <motion.div
                  className="bg-gray-50 rounded-lg p-2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={controls}
                  variants={{
                    reset: { opacity: 0, x: -20 },
                    animate: { opacity: 1, x: 0, transition: { delay: 1.5 } }
                  }}
                >
                  <h3 className="text-xs font-semibold text-gray-600 mb-1">Key Metrics</h3>
                  <div className="space-y-1.5">
                    <motion.div
                      className="h-1.5 bg-indigo-200 rounded-full overflow-hidden"
                      initial={{ width: "0%" }}
                      animate={controls}
                      variants={{
                        reset: { width: "0%" },
                        animate: { width: "85%", transition: { delay: 2, duration: 1 } }
                      }}
                    >
                      <div className="h-full bg-indigo-600 rounded-full" style={{ width: "85%" }}></div>
                    </motion.div>
                    <motion.div
                      className="h-1.5 bg-indigo-200 rounded-full overflow-hidden"
                      initial={{ width: "0%" }}
                      animate={controls}
                      variants={{
                        reset: { width: "0%" },
                        animate: { width: "92%", transition: { delay: 2.2, duration: 1 } }
                      }}
                    >
                      <div className="h-full bg-indigo-600 rounded-full" style={{ width: "92%" }}></div>
                    </motion.div>
                  </div>
                </motion.div>

                {/* Chart Section - Reduced height */}
                <motion.div
                  className="bg-gray-50 rounded-lg p-2 overflow-hidden"
                  initial={{ opacity: 0, x: 20 }}
                  animate={controls}
                  variants={{
                    reset: { opacity: 0, x: 20 },
                    animate: { opacity: 1, x: 0, transition: { delay: 1.5 } }
                  }}
                >
                  <motion.h3 
                    className="text-xs font-semibold text-gray-600 mb-1"
                    initial={{ opacity: 0 }}
                    animate={controls}
                    variants={{
                      reset: { opacity: 0 },
                      animate: { opacity: 1, transition: { delay: 1.7 } }
                    }}
                  >
                    Trend Analysis
                  </motion.h3>
                  
                  <svg className="w-full h-16" viewBox="0 0 280 100">
                    {/* Background grid with animation */}
                    <motion.rect
                      x="40" 
                      y="20" 
                      width="200" 
                      height="60"
                      fill="transparent"
                      stroke="#E5E7EB"
                      strokeWidth="0.5"
                      strokeDasharray="4 4"
                      initial={{ opacity: 0 }}
                      animate={controls}
                      variants={{
                        reset: { opacity: 0 },
                        animate: { opacity: 0.5, transition: { delay: 1.8 } }
                      }}
                    />
                    
                    {/* Grid lines */}
                    <motion.path
                      d="M40,20 L40,80 L240,80"
                      stroke="#E5E7EB"
                      strokeWidth="1"
                      fill="none"
                      initial={{ pathLength: 0 }}
                      animate={controls}
                      variants={{
                        reset: { pathLength: 0 },
                        animate: { pathLength: 1, transition: { delay: 1.9, duration: 0.8 } }
                      }}
                    />
                    
                    {/* Chart line */}
                    <motion.path
                      d={chartPoints}
                      stroke="#6366F1"
                      strokeWidth="2"
                      fill="none"
                      initial={{ pathLength: 0 }}
                      animate={controls}
                      variants={{
                        reset: { pathLength: 0 },
                        animate: { pathLength: 1, transition: { delay: 2.2, duration: 1.5 } }
                      }}
                    />

                    {/* Data points */}
                    {[40, 80, 120, 160, 200, 240].map((x, i) => (
                      <motion.circle
                        key={i}
                        cx={x}
                        cy={[80, 60, 70, 40, 50, 30][i]}
                        r="4"
                        fill="#6366F1"
                        initial={{ scale: 0 }}
                        animate={controls}
                        variants={{
                          reset: { scale: 0 },
                          animate: { scale: 1, transition: { delay: 2.2 + (i * 0.1) } }
                        }}
                      />
                    ))}
                    
                    {/* Animated highlight for the best data point */}
                    <motion.circle
                      cx="160"
                      cy="40"
                      r="6"
                      fill="transparent"
                      stroke="#6366F1"
                      strokeWidth="1.5"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={controls}
                      variants={{
                        reset: { opacity: 0, scale: 0.5 },
                        animate: { 
                          opacity: [0, 1, 0], 
                          scale: [0.5, 1.5, 0.5],
                          transition: { 
                            delay: 3.5, 
                            duration: 1.5, 
                            repeat: 1,
                            repeatType: "reverse"
                          } 
                        }
                      }}
                    />
                  </svg>
                </motion.div>
              </div>

              {/* Status Cards Row */}
              <motion.div
                className="grid grid-cols-3 gap-3"
                initial={{ opacity: 0, y: 20 }}
                animate={controls}
                variants={{
                  reset: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0, transition: { delay: 2.5 } }
                }}
              >
                {['Data Quality', 'SLA (Met)', 'Test Coverage'].map((title, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-2">
                    <div className="text-xs font-medium text-gray-600">{title}</div>
                    <motion.div
                      className="text-lg font-semibold text-indigo-600"
                      initial={{ opacity: 0 }}
                      animate={controls}
                      variants={{
                        reset: { opacity: 0 },
                        animate: { opacity: 1, transition: { delay: 3 + (i * 0.2) } }
                      }}
                    >
                      {95 - (i * 5)}%
                    </motion.div>
                  </div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Processing Indicator */}
        <motion.div
          className="absolute bottom-2 right-2 flex space-x-1"
          animate={{
            opacity: [0.3, 1, 0.3],
          }}
          transition={{
            repeat: Infinity,
            duration: 1.5,
            ease: "linear"
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1 h-1 bg-purple-500 rounded-full"
              style={{
                animationDelay: `${i * 0.2}s`
              }}
            />
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default DataLeaderAnimation; 